/**
 * Nickname Service
 * API functions for nickname management
 */

import { getIdToken } from "./firebase";
import { apiRequest } from "./queryClient";

export interface NicknameSuggestionsResponse {
  suggestions: string[];
}

export interface SetNicknameRequest {
  nickname: string;
  suburb?: string;
  regionCode?: string;
}

export interface SetNicknameResponse {
  success: boolean;
  nickname: string;
  nicknameDisplay: string;
  message: string;
}

/**
 * Get nickname suggestions
 */
export async function getNicknameSuggestions(
  suburbLabel?: string
): Promise<string[]> {
  const token = await getIdToken();
  if (!token) {
    throw new Error("로그인이 필요합니다");
  }

  const url = suburbLabel
    ? `/api/me/nickname/suggestions?suburbLabel=${encodeURIComponent(
        suburbLabel
      )}`
    : "/api/me/nickname/suggestions";

  try {
    const response = await apiRequest("GET", url, undefined, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const data: NicknameSuggestionsResponse = await response.json();
    return data.suggestions;
  } catch (error: any) {
    console.error("[nicknameService] Failed to fetch suggestions:", error);
    throw new Error(error.message || "닉네임 추천을 불러오는데 실패했습니다");
  }
}

/**
 * Set or update user nickname
 */
export async function setNickname(
  data: SetNicknameRequest
): Promise<SetNicknameResponse> {
  const token = await getIdToken();
  if (!token) {
    throw new Error("로그인이 필요합니다");
  }

  try {
    const response = await apiRequest("POST", "/api/me/nickname", data, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    const result = await response.json();

    // 응답에 에러가 있는지 확인
    if (result.error) {
      throw new Error(result.error);
    }

    return result;
  } catch (error: any) {
    console.error("[nicknameService] Failed to set nickname:", error);

    // 에러 메시지에서 실제 에러 내용 추출
    let errorMessage = error.message || "닉네임 설정에 실패했습니다";

    // HTML 응답인 경우 더 명확한 메시지 제공
    if (errorMessage.includes("<!DOCTYPE") || errorMessage.includes("<html")) {
      errorMessage = "서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.";
    }

    throw new Error(errorMessage);
  }
}
